import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCog, PackageCheck, CalendarDays, Megaphone, ListChecks,
  MessageSquare, CreditCard, ReceiptText, LifeBuoy, BarChart3, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "../../components/site/Navbar";
import { useAuth } from "../../lib/AuthContext";

const items = [
  ["Overview", "/admin", LayoutDashboard],
  ["Customers", "/admin/customers", Users],
  ["Brand Profiles", "/admin/brand-profiles", UserCog],
  ["Content Orders", "/admin/content", PackageCheck],
  ["Tasks", "/admin/tasks", ListChecks],
  ["Content Calendar", "/admin/calendar", CalendarDays],
  ["Promotions", "/admin/promotions", Megaphone],
  ["Revision Requests", "/admin/revisions", MessageSquare],
  ["Subscriptions", "/admin/subscriptions", CreditCard],
  ["Invoices", "/admin/invoices", ReceiptText],
  ["Support", "/admin/support", LifeBuoy],
  ["Analytics", "/admin/analytics", BarChart3],
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const nav = (
    <nav className="flex flex-col gap-1" data-testid="admin-sidebar-nav">
      {items.map(([label, to, Icon]) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/admin"}
          onClick={() => setOpen(false)}
          data-testid={`admin-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#F95738] text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {label}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={doLogout}
        data-testid="admin-logout-button"
        className="mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-red-500/20 hover:text-white"
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        Logout
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-40 bg-[#0F172A]">
        <div className="flex items-center justify-between px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 text-white lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/" aria-label="PostMint home">
              <Logo light />
            </Link>
            <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-white">
              Admin
            </span>
          </div>
          <span className="text-sm text-slate-300" data-testid="admin-user-label">{user?.email}</span>
        </div>
        {open && <div className="px-4 pb-4 lg:hidden" data-testid="admin-mobile-nav">{nav}</div>}
      </header>

      <div className="mx-auto flex w-full max-w-[1600px] gap-8 px-4 py-6 lg:px-6">
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="sticky top-24 rounded-2xl bg-[#0F172A] p-3">{nav}</div>
        </aside>
        <main className="min-w-0 flex-1 pb-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
